import React, { useState, useEffect } from 'react';
import useAdminAnalytics from '@/hooks/useAdminAnalytics';
import Pagination from '@/components/Pagination/index';
import '@/Pages/admin/Analytics/adminAnalyticsDashboard.css';

const AdminAnalyticsDashboard = () => {
  const { analytics, filterOptions, loading, getGlobalAnalytics, getFilterOptions } = useAdminAnalytics();
  const [filters, setFilters] = useState({
    userEmail: '',
    videoCategory: '',
    timeframe: '7:days'
  });
  const [emailSearchMode, setEmailSearchMode] = useState('all');
  const [emailSearchValue, setEmailSearchValue] = useState('');
  const [usersPagination, setUsersPagination] = useState({ page: 1, itemsPerPage: 10 });
  const [viewsPagination, setViewsPagination] = useState({ page: 1, itemsPerPage: 10 });
  const [categoriesPagination, setCategoriesPagination] = useState({ page: 1, itemsPerPage: 10 });
  const [videosPagination, setVideosPagination] = useState({ page: 1, itemsPerPage: 10 });

  useEffect(() => {
    getFilterOptions();
    getGlobalAnalytics(filters);
  }, []);

  const getPaginatedData = (data, pagination) => {
    const startIndex = (pagination.page - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const handlePageChange = (setPagination, page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleItemsPerPageChange = (setPagination, itemsPerPage) => {
    setPagination(prev => ({ ...prev, page: 1, itemsPerPage }));
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    getGlobalAnalytics(newFilters);
  };

  const getAccountTypeLabel = (user) => user?.accountContext?.userTypeLabel || 'User';
  const getStudioOwnerLabel = (user) => {
    const context = user?.accountContext;
    if (!context) return '-';
    if (context.userType === 'studio-owner') return context.email || '-';
    return context.studioOwnerEmail || '-';
  };

  if (loading && !analytics) {
    return <div className="loading">Loading analytics...</div>;
  }

  return (
    <div className="admin-analytics-dashboard">
      <h1>Platform Analytics Dashboard</h1>
      <div className="filter-bar">
        <div className="email-filter-container">
          <select 
            value={emailSearchMode}
            onChange={(e) => {
              setEmailSearchMode(e.target.value);
              if (e.target.value === 'all') {
                handleFilterChange('userEmail', '');
                setEmailSearchValue('');
              }
            }}
          >
            <option value="all">All Users</option>
            <option value="search">Search by Email</option>
          </select>
          {emailSearchMode === 'search' && (
            <input
              className="email-filter-input"
              type="email"
              placeholder="Enter user email..."
              value={emailSearchValue}
              onChange={(e) => setEmailSearchValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleFilterChange('userEmail', emailSearchValue);
                }
              }}
              onBlur={() => handleFilterChange('userEmail', emailSearchValue)}
            />
          )}
        </div>
        <select 
          value={filters.videoCategory} 
          onChange={(e) => handleFilterChange('videoCategory', e.target.value)}
        >
          <option value="">All Categories</option>
          {filterOptions.categories.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
        <select 
          value={filters.timeframe} 
          onChange={(e) => handleFilterChange('timeframe', e.target.value)}
        >
          <option value="1:day">Last 24 Hours</option>
          <option value="7:days">Last 7 Days</option>
          <option value="30:days">Last 30 Days</option>
          <option value="100:days">Last 100 Days</option>
        </select>
      </div>
      {analytics && (
        <>
          <div className="stats-cards">
            <div className="stat-card">
              <h3>Total Views</h3>
              <div className="stat-number">{analytics.totalViews.toLocaleString()}</div>
            </div>
            <div className="stat-card">
              <h3>Unique Users</h3>
              <div className="stat-number">{analytics.uniqueUsersCount}</div>
            </div>
            <div className="stat-card">
              <h3>Total Watch Time</h3>
              <div className="stat-number">{analytics.totalWatchTimeFormatted}</div>
            </div>
          </div>
          {analytics.searchedUserContext && (
            <div className="searched-user-context">
              <div>
                <span className="searched-user-context__label">Searched Account</span>
                <strong>{analytics.searchedUserContext.email}</strong>
              </div>
              <div>
                <span className="searched-user-context__label">Account Type</span>
                <strong>{analytics.searchedUserContext.userTypeLabel}</strong>
              </div>
              {analytics.searchedUserContext.userType === 'subuser' && (
                <div>
                  <span className="searched-user-context__label">Studio Owner</span>
                  <strong>{analytics.searchedUserContext.studioOwnerEmail || '-'}</strong>
                </div>
              )}
            </div>
          )}
          <div className="analytics-section">
            <h2>Top Users by Watch Time</h2>
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>User Email</th>
                    <th>Account Type</th>
                    <th>Studio Owner</th>
                    <th>Total Watch Time</th>
                    <th>Total Views</th>
                  </tr>
                </thead>
                <tbody>
                  {getPaginatedData(analytics.userStatsArray, usersPagination).map(user => (
                    <tr key={user.email}>
                      <td className="analytics-table__email">{user.email}</td>
                      <td>{getAccountTypeLabel(user)}</td>
                      <td className="analytics-table__email">{getStudioOwnerLabel(user)}</td>
                      <td>{user.totalWatchTimeFormatted}</td>
                      <td>{user.views}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={usersPagination.page}
              totalItems={analytics.userStatsArray.length}
              itemsPerPage={usersPagination.itemsPerPage}
              onPageChange={(page) => handlePageChange(setUsersPagination, page)}
              onItemsPerPageChange={(itemsPerPage) => handleItemsPerPageChange(setUsersPagination, itemsPerPage)}
            />
          </div>

          <div className="analytics-section">
            <h2>Popular Categories</h2>
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Total Watch Time</th>
                  <th>Views</th>
                </tr>
              </thead>
              <tbody>
                {getPaginatedData(analytics.categoryStatsArray, categoriesPagination).map(category => (
                  <tr key={category.category}>
                    <td>{category.category}</td>
                    <td>{category.totalWatchTimeFormatted}</td>
                    <td>{category.views}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              currentPage={categoriesPagination.page}
              totalItems={analytics.categoryStatsArray.length}
              itemsPerPage={categoriesPagination.itemsPerPage}
              onPageChange={(page) => handlePageChange(setCategoriesPagination, page)}
              onItemsPerPageChange={(itemsPerPage) => handleItemsPerPageChange(setCategoriesPagination, itemsPerPage)}
            />
          </div>

          <div className="analytics-section">
            <h2>Top Videos by Views</h2>
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Video Title</th>
                  <th>Category</th>
                  <th>Total Views</th>
                  <th>Avg Watch Time</th>
                </tr>
              </thead>
              <tbody>
                {getPaginatedData(analytics.videoPopularityStats || [], videosPagination).map((video, index) => (
                  <tr key={index}>
                    <td>{video.videoTitle}</td>
                    <td>{video.category}</td>
                    <td>{video.views}</td>
                    <td>{video.avgWatchTimeFormatted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              currentPage={videosPagination.page}
              totalItems={(analytics.videoPopularityStats || []).length}
              itemsPerPage={videosPagination.itemsPerPage}
              onPageChange={(page) => handlePageChange(setVideosPagination, page)}
              onItemsPerPageChange={(itemsPerPage) => handleItemsPerPageChange(setVideosPagination, itemsPerPage)}
            />
          </div>
          {filters.userEmail && (
            <div className="analytics-section">
              <h2>Recent Views</h2>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>User Email</th>
                    <th>Video Title</th>
                    <th>Category</th>
                    <th>Watch Duration</th>
                    <th>Total Duration</th>
                    <th>Completion Rate</th>
                    <th>Device</th>
                    <th>Viewed At</th>
                  </tr>
                </thead>
                <tbody>
                  {getPaginatedData(analytics.detailedViews, viewsPagination).map((view, index) => (
                    <tr key={index}>
                      <td>{view.userEmail}</td>
                      <td>{view.videoTitle}</td>
                      <td>{view.category}</td>
                      <td>{view.watchDurationFormatted}</td>
                      <td>{view.totalDurationFormatted}</td>
                      <td>{view.completionPercentage}%</td>
                      <td>{view.device}</td>
                      <td>{new Date(view.viewStart).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                currentPage={viewsPagination.page}
                totalItems={analytics.detailedViews.length}
                itemsPerPage={viewsPagination.itemsPerPage}
                onPageChange={(page) => handlePageChange(setViewsPagination, page)}
                onItemsPerPageChange={(itemsPerPage) => handleItemsPerPageChange(setViewsPagination, itemsPerPage)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminAnalyticsDashboard;
